/* eslint-disable react/no-array-index-key */
/** @jsxImportSource @emotion/react */

import { memo } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import {
  TableContainer,
  Table as TableComp,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@material-ui/core";

import { Img } from "..";
import { Pick as PickType, objToArray } from "../../utils/parsers";

import { table, row, cell, content, img, dot, txt as txtStyle } from "./styles";

interface BodyRow
  extends Pick<PickType, "key" | "imgKey" | "convertDateTime" | "extraRender" | "css" | "txtCss" | "contentCss"> {
  status?: boolean;
}

interface ActionCell {
  cellWidth?: string;
  renderCell: (rowData: any) => React.ReactChild;
}
export interface Props {
  headCells: string[];
  bodyRows: any[];
  bodyCells: BodyRow[];
  cellWidths?: string[];
  baseLinkPath?: string;
  statusColor?: Record<string, any>;
  minWidth?: string;
  actionCell?: ActionCell;
  selectionCell?: ActionCell & { selectionInHead?: boolean };
  onClick?: (rowData: any) => void;
}

const Table = ({
  bodyRows,
  headCells,
  bodyCells,
  cellWidths = [],
  baseLinkPath,
  statusColor,
  minWidth,
  actionCell,
  selectionCell,
  onClick,
}: Props): JSX.Element | null => {
  const intl = useIntl();
  const {
    push,
    location: { pathname, search },
  } = useHistory();

  const renderSelectionCell = ({ visible = true, data = {} }): JSX.Element => (
    <TableCell key="cell-section" css={selectionCell?.cellWidth && cell(selectionCell?.cellWidth)}>
      {visible && selectionCell?.renderCell(data)}
    </TableCell>
  );

  const renderTableHeadCells = (): JSX.Element[] =>
    headCells.map((txt, i) => <TableCell key={`cell-${i}`}>{txt}</TableCell>);

  const renderTableBodyRows = (): JSX.Element[] =>
    bodyRows.map((data: any) => {
      const props: Record<string, any> = { key: data.id };
      if (baseLinkPath || onClick) {
        props.css = row;
        props.onClick = (): void => {
          if (baseLinkPath) {
            push(`${baseLinkPath}/${data.id}`, {
              from: pathname + search,
            });
          } else {
            onClick?.(data);
          }
        };
      }

      return (
        <TableRow {...props}>
          {selectionCell && renderSelectionCell({ data })}
          {objToArray(data, bodyCells, intl).map(({ imgUrl, txt, status, extraRender, css, txtCss, contentCss }, i) => (
            <TableCell key={`cell-${i}`} css={[cellWidths[i] && cell(cellWidths[i]), css].filter(Boolean)}>
              <div css={[content, contentCss?.(data)].filter(Boolean)}>
                {imgUrl !== undefined && <Img css={img} src={imgUrl} alt={txt} />}
                <span css={[txtStyle, statusColor && status && dot(statusColor[txt]), txtCss?.(data)].filter(Boolean)}>
                  {txt}
                </span>
                {extraRender && extraRender(data)}
              </div>
            </TableCell>
          ))}
          {actionCell && (
            <TableCell key="cell-action" css={actionCell.cellWidth && cell(actionCell.cellWidth)}>
              {actionCell.renderCell(data)}
            </TableCell>
          )}
        </TableRow>
      );
    });

  return bodyRows?.length ? (
    <TableContainer component={Paper} elevation={3}>
      <TableComp css={table(minWidth)}>
        <TableHead>
          <TableRow>
            {selectionCell && renderSelectionCell({ visible: selectionCell?.selectionInHead })}
            {renderTableHeadCells()}
            {actionCell && <TableCell key="cell-action" />}
          </TableRow>
        </TableHead>
        <TableBody>{renderTableBodyRows()}</TableBody>
      </TableComp>
    </TableContainer>
  ) : null;
};

export default memo(Table);
